import { Chromosome, Particle, QoSProperty, QoS_WEIGHTS, QoS_MAXIMIZE } from '../types/algorithm';
import type { EnergyAwareWeights, FitnessMode } from '../types';
import { createSeededRNG, SeededRNG } from './seededRNG';
import { incrementFitnessEvaluations } from './benchmarkInstrumentation';

/**
 * Gets quality value from matrix
 * matrix[task][service][property]
 * 
 * CRITICAL: Returns NaN (not 0) for missing data to avoid injecting fake zeros.
 * Missing data should be ignored in min/max computation and aggregation.
 * Only use 0 if the dataset explicitly defines 0 as a real value (not the case in QWS).
 */
function getQualityValue(
  matrix: number[][][],
  task: number,
  service: number,
  property: number
): number {
  if (matrix[task] && matrix[task][service] && matrix[task][service][property] !== undefined) {
    const value = matrix[task][service][property];
    // Return NaN for missing/invalid data instead of 0
    // This prevents fake zeros from corrupting min/max ranges and aggregations
    return Number.isFinite(value) ? value : NaN;
  }
  return NaN; // Missing data, not 0
}

/**
 * Normalizes a value to [0, 1] range
 * For maximize: normalizes to [0, 1] where 1 is best
 * For minimize: normalizes to [1, 0] where 0 is best (inverted)
 * 
 * CRITICAL: Clamps output to [0, 1] to handle cases where value is outside [min, max] range.
 * This prevents normalization from producing values outside the expected bounds.
 */
function normalizeValue(
  value: number,
  min: number,
  max: number,
  maximize: boolean
): number {
  if (max === min) return 0.5; // Avoid division by zero

  let normalized: number;
  if (maximize) {
    normalized = (value - min) / (max - min);
  } else {
    // For minimization, invert: best (min) becomes 1, worst (max) becomes 0
    normalized = (max - value) / (max - min);
  }
  
  // Clamp to [0, 1] to handle out-of-range values
  return Math.max(0, Math.min(1, normalized));
}

/**
 * Computes sum of QoS_WEIGHTS for normalization
 */
function computeWeightSum(): number {
  return QoS_WEIGHTS.reduce((sum, weight) => sum + weight, 0);
}

/**
 * Aggregation method for sequential workflow composition
 * Based on standard service composition literature:
 * - Response Time: SUM (additive)
 * - Availability: PRODUCT (probability of all services being available)
 * - Throughput: MIN (bottleneck determines overall throughput)
 * - Successability: PRODUCT (probability of all succeeding)
 * - Reliability: PRODUCT (probability of all being reliable)
 * - Compliance: PRODUCT (all must comply)
 * - Best Practices: PRODUCT (all must follow best practices)
 * - Latency: SUM (additive)
 * - Documentation: PRODUCT (all must be documented)
 */
type AggregationMethod = 'sum' | 'product' | 'min';

const QoS_AGGREGATION_METHOD: AggregationMethod[] = [
  'sum',      // ResponseTime - sum
  'product',  // Availability - product
  'min',      // Throughput - min (bottleneck)
  'product',  // Successability - product
  'product',  // Reliability - product
  'product',  // Compliance - product
  'product',  // BestPractices - product
  'sum',      // Latency - sum
  'product',  // Documentation - product
];

const PRODUCT_QOS_EPSILON = 1e-12;

/**
 * Product-style QoS attributes in QWS are percentage-like values (typically 0-100).
 * For sequential probability aggregation they must be converted to [0,1] first.
 *
 * The returned value is clamped away from exact zero so very large workflows do not
 * collapse to underflow when aggregated in log-space.
 */
function normalizeProductOperand(value: number): number {
  if (!Number.isFinite(value)) {
    return NaN;
  }

  const scaled = value > 1 ? value / 100 : value;
  return Math.max(PRODUCT_QOS_EPSILON, Math.min(1, scaled));
}

function getProductLogValue(value: number): number {
  const operand = normalizeProductOperand(value);
  return Number.isFinite(operand) ? Math.log(operand) : NaN;
}

export const DEFAULT_ENERGY_AWARE_WEIGHTS: EnergyAwareWeights = {
  fitness: 0.625,
  energy: 0.375,
};

/**
 * Cached Fitness Calculator - Precomputes min/max values for performance
 * CRITICAL OPTIMIZATION: Precomputes property ranges once instead of recalculating on every fitness call
 * This provides 10-100x speedup for large problems
 * 
 * ADDITIONAL OPTIMIZATION: Caches fitness values for duplicate chromosomes (1.5-3x additional speedup)
 * 
 * ENERGY-AWARE MODE: Supports composite fitness including QoS and energy objectives
 */
export class FitnessCalculator {
  private propertyRanges: Array<{ min: number; max: number }> | null = null;
  private aggregatedPropertyRanges: Array<{ min: number; max: number }> | null = null; // Ranges for aggregated values
  private energyRange: { min: number; max: number; hasValid: boolean } | null = null;
  private matrix: number[][][] | null = null;
  private taskCount: number = 0;
  private serviceCount: number = 0;
  private fitnessCache: Map<string, number> = new Map(); // Cache for duplicate chromosomes
  private maxCacheSize: number = 10000; // Limit cache size to prevent memory issues
  private weightSum: number = computeWeightSum(); // Compute weight sum once
  private mode: FitnessMode = 'qos-only';
  private energyAwareWeights: EnergyAwareWeights = DEFAULT_ENERGY_AWARE_WEIGHTS;
  private costCalculator: CostCalculator | null = null;
  private rng: SeededRNG | null = null; // Seeded RNG for reproducible sampling

  /**
   * Initialize calculator with matrix - precomputes property ranges
   * @param matrix Quality matrix
   * @param taskCount Number of tasks
   * @param serviceCount Number of services
   * @param mode Fitness calculation mode ('qos-only' or 'energy-aware')
   * @param energyAwareWeights Weights for energy-aware mode (only used if mode is 'energy-aware')
   * @param costCalculator Cost calculator instance retained for reporting compatibility
   * @param seed Optional seed for RNG (for reproducibility). If not provided, uses timestamp.
   */
  initialize(
    matrix: number[][][],
    taskCount: number,
    serviceCount: number,
    mode: FitnessMode = 'qos-only',
    energyAwareWeights: EnergyAwareWeights = DEFAULT_ENERGY_AWARE_WEIGHTS,
    costCalculator: CostCalculator | null = null,
    seed?: number
  ): void {
    this.matrix = matrix;
    this.taskCount = taskCount;
    this.serviceCount = serviceCount;
    this.mode = mode;
    this.energyAwareWeights = energyAwareWeights;
    this.costCalculator = costCalculator;
    
    // Initialize seeded RNG for reproducible sampling
    this.rng = createSeededRNG(seed);
    
    // Clear cache when reinitializing
    this.fitnessCache.clear();
    
    // Precompute property ranges ONCE (this was the bottleneck!)
    this.propertyRanges = [];
    for (let prop = 0; prop < 9; prop++) {
      let min = Infinity;
      let max = -Infinity;

      // Find min/max across ALL services for ALL tasks (global normalization)
      // CRITICAL: Use Number.isFinite() to skip NaN/Infinity completely
      // Missing data (NaN) is ignored, preventing fake zeros from corrupting ranges
      for (let task = 0; task < taskCount; task++) {
        for (let service = 0; service < serviceCount; service++) {
          const value = getQualityValue(matrix, task, service, prop);
          if (Number.isFinite(value)) {
            min = Math.min(min, value);
            max = Math.max(max, value);
          }
        }
      }

      // Fallback if no valid values found
      if (min === Infinity || max === -Infinity) {
        min = 0;
        max = 1;
      }
      
      this.propertyRanges.push({ min, max });
    }

    // Precompute aggregated property ranges for workflow-aware normalization
    this.precomputeAggregatedPropertyRanges();

    // Precompute energy range for energy-aware mode
    if (mode === 'energy-aware') {
      this.precomputeEnergyRange();
    } else {
      this.energyRange = null;
    }
  }

  /**
   * Precomputes aggregated property ranges for workflow-aware normalization
   * Uses sampling to estimate min/max of aggregated values across all possible solutions
   * 
   * For sequential workflow composition:
   * - SUM aggregation: theoretical range is [n*min_raw, n*max_raw]
   * - PRODUCT aggregation: theoretical range is [min_raw^n, max_raw^n] (for positive values)
   * - MIN aggregation: theoretical range is [min_raw, max_raw]
   * 
   * However, we use sampling for accuracy, especially for product aggregation where
   * the actual range depends on the distribution of values.
   */
  private precomputeAggregatedPropertyRanges(): void {
    if (!this.matrix || !this.propertyRanges) return;

    this.aggregatedPropertyRanges = [];
    
    // For each property, compute the range of aggregated values
    for (let prop = 0; prop < 9; prop++) {
      const method = QoS_AGGREGATION_METHOD[prop];
      const { min: minRaw, max: maxRaw } = this.propertyRanges[prop];
      
      // Compute theoretical bounds based on aggregation method
      let aggregatedMin: number;
      let aggregatedMax: number;
      
      if (method === 'sum') {
        // SUM: [n * min_raw, n * max_raw]
        aggregatedMin = this.taskCount * minRaw;
        aggregatedMax = this.taskCount * maxRaw;
      } else if (method === 'product') {
        // PRODUCT attributes are aggregated in log-space for numerical stability.
        // QWS stores these values as percentages, so convert them to probabilities first.
        aggregatedMin = 0;
        aggregatedMax = 0;
        let hasValidProductRange = false;

        for (let task = 0; task < this.taskCount; task++) {
          let taskMinLog = Infinity;
          let taskMaxLog = -Infinity;

          for (let serviceIdx = 0; serviceIdx < this.serviceCount; serviceIdx++) {
            const value = getQualityValue(this.matrix, task, serviceIdx, prop);
            const logValue = getProductLogValue(value);
            if (Number.isFinite(logValue)) {
              taskMinLog = Math.min(taskMinLog, logValue);
              taskMaxLog = Math.max(taskMaxLog, logValue);
              hasValidProductRange = true;
            }
          }

          if (taskMinLog !== Infinity && taskMaxLog !== -Infinity) {
            aggregatedMin += taskMinLog;
            aggregatedMax += taskMaxLog;
          }
        }

        if (!hasValidProductRange) {
          aggregatedMin = Math.log(PRODUCT_QOS_EPSILON);
          aggregatedMax = 0;
        }
      } else if (method === 'min') {
        // MIN: [min_raw, max_raw]
        aggregatedMin = minRaw;
        aggregatedMax = maxRaw;
      } else {
        // Fallback
        aggregatedMin = minRaw;
        aggregatedMax = maxRaw;
      }
      
      // Ensure min <= max
      if (aggregatedMin > aggregatedMax) {
        const temp = aggregatedMin;
        aggregatedMin = aggregatedMax;
        aggregatedMax = temp;
      }

      if (!Number.isFinite(aggregatedMin) || !Number.isFinite(aggregatedMax)) {
        if (method === 'product') {
          aggregatedMin = Math.log(PRODUCT_QOS_EPSILON);
          aggregatedMax = 0;
        } else {
          aggregatedMin = 0;
          aggregatedMax = 1;
        }
      }

      // Add small padding to handle edge cases
      const padding = (aggregatedMax - aggregatedMin) * 0.01;
      this.aggregatedPropertyRanges.push({
        min: method === 'product' ? aggregatedMin - padding : Math.max(0, aggregatedMin - padding),
        max: aggregatedMax + padding
      });
    }
  }

  /**
   * Precomputes energy range using deterministic per-task extremes
   * This ensures reproducible normalization bounds across runs
   * 
   * DETERMINISTIC APPROACH (thesis-safe):
   * - Energy bounds: For each task, find minEnergyService and maxEnergyService
   *   Global minEnergy = sum(minEnergyService), Global maxEnergy = sum(maxEnergyService)
   * 
   * This keeps energy-aware fitness deterministic and defensible for thesis defense.
   */
  private precomputeEnergyRange(): void {
    if (!this.matrix) return;

    const alpha = 0.6;  // Weight for response time (matches calculateEnergy)
    const beta = 0.4;   // Weight for latency (matches calculateEnergy)
    const P = 3.0;      // Power consumption in Watts (matches calculateEnergy)

    // Compute deterministic energy bounds using per-task extremes
    let energyMin = 0;
    let energyMax = 0;
    let hasValidEnergy = false;

    for (let task = 0; task < this.taskCount; task++) {
      let taskMinEnergy = Infinity;
      let taskMaxEnergy = -Infinity;

      // Find min/max energy for this task across all services
      for (let serviceIdx = 0; serviceIdx < this.serviceCount; serviceIdx++) {
        const rt = getQualityValue(this.matrix, task, serviceIdx, QoSProperty.ResponseTime);
        const lat = getQualityValue(this.matrix, task, serviceIdx, QoSProperty.Latency);
        
        // Only compute energy if BOTH RT and Latency are finite (matches calculateEnergy logic)
        if (Number.isFinite(rt) && Number.isFinite(lat)) {
          const energy = ((alpha * rt + beta * lat) / 1000) * P;
          taskMinEnergy = Math.min(taskMinEnergy, energy);
          taskMaxEnergy = Math.max(taskMaxEnergy, energy);
          hasValidEnergy = true;
        }
      }

      // Sum per-task extremes to get global bounds
      if (taskMinEnergy !== Infinity && taskMaxEnergy !== -Infinity) {
        energyMin += taskMinEnergy;
        energyMax += taskMaxEnergy;
      }
    }

    // Set energy range
    if (!hasValidEnergy) {
      this.energyRange = { min: 0, max: 1, hasValid: false };
    } else {
      // Ensure min < max (add small padding if they're equal)
      if (energyMin >= energyMax) {
        energyMax = energyMin + 1;
      }
      this.energyRange = { 
        min: Math.max(0, energyMin), 
        max: energyMax, 
        hasValid: true 
      };
    }

  }

  /**
   * Clears the fitness cache (useful for memory management)
   */
  clearCache(): void {
    this.fitnessCache.clear();
  }

  /**
   * Gets cache statistics (for debugging/monitoring)
   */
  getCacheStats(): { size: number; maxSize: number; hitRate?: number } {
    return {
      size: this.fitnessCache.size,
      maxSize: this.maxCacheSize,
    };
  }

  /**
   * Calculates fitness using cached property ranges and fitness cache
   * 
   * QoS-only mode: Fitness formula: (Σ(weight[i] * normalized_quality[i])) / sum(QoS_WEIGHTS)
   * Energy-aware mode: Composite fitness = w_fitness * QoS_norm + w_energy * Energy_norm
   * 
   * Result is in [0, 1] range
   * 
   * PERFORMANCE OPTIMIZATION: Caches fitness values for duplicate chromosomes
   */
  calculateFitness(solution: number[]): number {
    if (!this.propertyRanges || !this.matrix) {
      throw new Error('FitnessCalculator not initialized. Call initialize() first.');
    }

    // For very large chromosomes, serializing the full solution into a cache key
    // is often more expensive than the cache hit it enables.
    const shouldUseCache = solution.length <= 300;
    const cacheKey = shouldUseCache ? `${this.mode}:${solution.join(',')}` : null;

    if (cacheKey && this.fitnessCache.has(cacheKey)) {
      return this.fitnessCache.get(cacheKey)!;
    }

    // Calculate QoS fitness (always needed)
    const qosFitness = this.calculateQoSFitness(solution);

    // If QoS-only mode, return QoS fitness
    if (this.mode === 'qos-only') {
      // Cache the result
      if (cacheKey) {
        if (this.fitnessCache.size < this.maxCacheSize) {
          this.fitnessCache.set(cacheKey, qosFitness);
        } else {
          this.fitnessCache.clear();
          this.fitnessCache.set(cacheKey, qosFitness);
        }
      }
      return qosFitness;
    }

    // Energy-aware mode: calculate composite fitness
    if (this.mode === 'energy-aware') {
      // Calculate energy
      const energy = calculateEnergy(solution, this.matrix, this.taskCount, this.serviceCount);

      // Normalize energy (minimize objective - invert)
      let energyNorm = 0;
      if (this.energyRange && this.energyRange.hasValid && Number.isFinite(energy)) {
        const energyNormRaw = normalizeValue(energy, this.energyRange.min, this.energyRange.max, false);
        energyNorm = Number.isFinite(energyNormRaw) ? energyNormRaw : 0; // Already inverted in normalizeValue for minimize
      }

      // Normalize weights to sum to 1.0
      const totalWeight = this.energyAwareWeights.fitness + this.energyAwareWeights.energy;
      const wFitness = totalWeight > 0 ? this.energyAwareWeights.fitness / totalWeight : DEFAULT_ENERGY_AWARE_WEIGHTS.fitness;
      const wEnergy = totalWeight > 0 ? this.energyAwareWeights.energy / totalWeight : DEFAULT_ENERGY_AWARE_WEIGHTS.energy;

      // Composite fitness: w_fitness * QoS + w_energy * Energy_norm
      // All components are in [0, 1] range (higher is better)
      const compositeFitness = wFitness * qosFitness + wEnergy * energyNorm;

      // Cache the result
      if (cacheKey) {
        if (this.fitnessCache.size < this.maxCacheSize) {
          this.fitnessCache.set(cacheKey, compositeFitness);
        } else {
          this.fitnessCache.clear();
          this.fitnessCache.set(cacheKey, compositeFitness);
        }
      }

      return compositeFitness;
    }

    // Fallback to QoS-only
    return qosFitness;
  }

  /**
   * Calculates QoS-only fitness using workflow-aware aggregation
   * 
   * WORKFLOW-AWARE AGGREGATION (Sequential Composition):
   * 1. Aggregate raw values per property using workflow-aware method (sum/product/min)
   * 2. Normalize the aggregated result
   * 3. Apply weights
   * 
   * This aligns with standard service composition literature where different QoS properties
   * use different aggregation methods for sequential workflows:
   * - Response Time, Latency: SUM (additive)
   * - Availability, Reliability, Successability: PRODUCT (multiplicative)
   * - Throughput: MIN (bottleneck)
   */
  calculateQoSFitness(solution: number[]): number {
    if (!this.propertyRanges || !this.aggregatedPropertyRanges || !this.matrix) {
      throw new Error('FitnessCalculator not initialized. Call initialize() first.');
    }

    const aggregatedValues = new Array(9).fill(0);
    const validCounts = new Array(9).fill(0);

    for (let task = 0; task < this.taskCount; task++) {
      const serviceIdx = solution[task];
      if (serviceIdx < 0 || serviceIdx >= this.serviceCount) {
        continue;
      }

      const serviceValues = this.matrix[task]?.[serviceIdx];
      if (!serviceValues) {
        continue;
      }

      for (let prop = 0; prop < 9; prop++) {
        const method = QoS_AGGREGATION_METHOD[prop];
        const value = serviceValues[prop];
        if (!Number.isFinite(value)) {
          continue;
        }

        switch (method) {
          case 'sum':
            validCounts[prop]++;
            aggregatedValues[prop] += value;
            break;
          case 'product':
            {
              const logValue = getProductLogValue(value);
              if (!Number.isFinite(logValue)) {
                break;
              }
              validCounts[prop]++;
              aggregatedValues[prop] += logValue;
            }
            break;
          case 'min':
            validCounts[prop]++;
            aggregatedValues[prop] =
              validCounts[prop] === 1 ? value : Math.min(aggregatedValues[prop], value);
            break;
          default:
            break;
        }
      }
    }

    let weightedSum = 0;
    for (let prop = 0; prop < 9; prop++) {
      if (validCounts[prop] === 0) {
        continue;
      }

      const aggregatedValue = aggregatedValues[prop];
      if (!Number.isFinite(aggregatedValue)) {
        continue;
      }

      const { min: aggMin, max: aggMax } = this.aggregatedPropertyRanges[prop];
      const normalizedValue = normalizeValue(
        aggregatedValue,
        aggMin,
        aggMax,
        QoS_MAXIMIZE[prop]
      );

      weightedSum += QoS_WEIGHTS[prop] * normalizedValue;
    }

    // Final fitness: weightedSum / weightSum (normalize by weight sum to get [0, 1] range)
    return weightedSum / this.weightSum;
  }
}

/**
 * Legacy function - kept for backward compatibility
 * NOTE: This still recalculates min/max every time (slow for large problems)
 * Consider using FitnessCalculator class instead for better performance
 * 
 * MUST match FitnessCalculator logic exactly: NaN skipping, clamping, [0,1] fitness
 */
export function calculateFitness(
  solution: number[], // Service selection array: solution[i] = service index for task i
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  // First pass: find GLOBAL min/max for normalization across ALL services
  // This ensures fitness values are comparable across different solutions
  const propertyRanges: Array<{ min: number; max: number }> = [];
  for (let prop = 0; prop < 9; prop++) {
    let min = Infinity;
    let max = -Infinity;

    // Find min/max across ALL services for ALL tasks (global normalization)
    // CRITICAL: Use Number.isFinite() to skip NaN/Infinity completely
    // Missing data (NaN) is ignored, preventing fake zeros from corrupting ranges
    for (let task = 0; task < taskCount; task++) {
      for (let service = 0; service < serviceCount; service++) {
        const value = getQualityValue(matrix, task, service, prop);
        if (Number.isFinite(value)) {
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      }
    }

    // Fallback if no valid values found
    if (min === Infinity || max === -Infinity) {
      min = 0;
      max = 1;
    }
    
    propertyRanges.push({ min, max });
  }

  // Compute aggregated property ranges for workflow-aware normalization
  const aggregatedPropertyRanges: Array<{ min: number; max: number }> = [];
  for (let prop = 0; prop < 9; prop++) {
    const method = QoS_AGGREGATION_METHOD[prop];
    const { min: minRaw, max: maxRaw } = propertyRanges[prop];
    
    let aggregatedMin: number;
    let aggregatedMax: number;
    
    if (method === 'sum') {
      // SUM: [n * min_raw, n * max_raw]
      aggregatedMin = taskCount * minRaw;
      aggregatedMax = taskCount * maxRaw;
    } else if (method === 'product') {
      aggregatedMin = 0;
      aggregatedMax = 0;
      let hasValidProductRange = false;

      for (let task = 0; task < taskCount; task++) {
        let taskMinLog = Infinity;
        let taskMaxLog = -Infinity;

        for (let serviceIdx = 0; serviceIdx < serviceCount; serviceIdx++) {
          const value = getQualityValue(matrix, task, serviceIdx, prop);
          const logValue = getProductLogValue(value);
          if (Number.isFinite(logValue)) {
            taskMinLog = Math.min(taskMinLog, logValue);
            taskMaxLog = Math.max(taskMaxLog, logValue);
            hasValidProductRange = true;
          }
        }

        if (taskMinLog !== Infinity && taskMaxLog !== -Infinity) {
          aggregatedMin += taskMinLog;
          aggregatedMax += taskMaxLog;
        }
      }

      if (!hasValidProductRange) {
        aggregatedMin = Math.log(PRODUCT_QOS_EPSILON);
        aggregatedMax = 0;
      }
    } else if (method === 'min') {
      // MIN: [min_raw, max_raw]
      aggregatedMin = minRaw;
      aggregatedMax = maxRaw;
    } else {
      aggregatedMin = minRaw;
      aggregatedMax = maxRaw;
    }
    
    // Ensure min <= max and add padding
    if (aggregatedMin > aggregatedMax) {
      const temp = aggregatedMin;
      aggregatedMin = aggregatedMax;
      aggregatedMax = temp;
    }
    if (!Number.isFinite(aggregatedMin) || !Number.isFinite(aggregatedMax)) {
      if (method === 'product') {
        aggregatedMin = Math.log(PRODUCT_QOS_EPSILON);
        aggregatedMax = 0;
      } else {
        aggregatedMin = 0;
        aggregatedMax = 1;
      }
    }

    const padding = (aggregatedMax - aggregatedMin) * 0.01;
    aggregatedPropertyRanges.push({
      min: method === 'product' ? aggregatedMin - padding : Math.max(0, aggregatedMin - padding),
      max: aggregatedMax + padding
    });
  }

  // Second pass: workflow-aware aggregation
  // Step 1: Aggregate raw values per property using workflow-aware method
  // Step 2: Normalize the aggregated result
  const normalizedValues: number[] = new Array(9).fill(0);
  for (let prop = 0; prop < 9; prop++) {
    const method = QoS_AGGREGATION_METHOD[prop];
    const maximize = QoS_MAXIMIZE[prop];
    
    let hasValid = false;
    let aggregatedValue = method === 'min' ? Infinity : 0;

    for (let task = 0; task < taskCount; task++) {
      const serviceIdx = solution[task];
      if (serviceIdx >= 0 && serviceIdx < serviceCount) {
        const value = getQualityValue(matrix, task, serviceIdx, prop);
        if (!Number.isFinite(value)) {
          continue;
        }

        if (method === 'sum') {
          aggregatedValue += value;
          hasValid = true;
        } else if (method === 'product') {
          const logValue = getProductLogValue(value);
          if (Number.isFinite(logValue)) {
            aggregatedValue += logValue;
            hasValid = true;
          }
        } else if (method === 'min') {
          aggregatedValue = hasValid ? Math.min(aggregatedValue, value) : value;
          hasValid = true;
        }
      }
    }
    
    // Aggregate raw values using workflow-aware method
    if (!hasValid) {
      normalizedValues[prop] = 0;
      continue;
    }

    // Normalize the aggregated result (not individual values)
    if (!Number.isFinite(aggregatedValue)) {
      normalizedValues[prop] = 0;
      continue;
    }
    
    const { min: aggMin, max: aggMax } = aggregatedPropertyRanges[prop];
    normalizedValues[prop] = normalizeValue(aggregatedValue, aggMin, aggMax, maximize);
  }

  // Step 3: Calculate weighted sum
  let weightedSum = 0;
  for (let prop = 0; prop < 9; prop++) {
    weightedSum += QoS_WEIGHTS[prop] * normalizedValues[prop];
  }

  // Final fitness: weightedSum / weightSum (normalize by weight sum to get [0, 1] range)
  const weightSum = computeWeightSum();
  const fitness = weightedSum / weightSum;
  return fitness;
}

// Global calculator instance (set by optimizationService for performance)
let globalCalculator: FitnessCalculator | null = null;

/**
 * Sets the global fitness calculator (called by optimizationService)
 */
export function setGlobalFitnessCalculator(calculator: FitnessCalculator): void {
  globalCalculator = calculator;
}

/**
 * Clears the global fitness calculator
 */
export function clearGlobalFitnessCalculator(): void {
  globalCalculator = null;
}

/**
 * Calculates fitness for chromosome
 * Uses global calculator if available (fast), otherwise falls back to legacy method (slow)
 */
export function calculateChromosomeFitness(
  chromosome: Chromosome,
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  incrementFitnessEvaluations();
  let fitness: number;
  
  if (globalCalculator) {
    // Use optimized cached calculator (FAST - 10-100x faster)
    fitness = globalCalculator.calculateFitness(chromosome.x);
  } else {
    // Legacy mode - recalculates min/max every time (SLOW)
    fitness = calculateFitness(chromosome.x, matrix, taskCount, serviceCount);
  }
  
  chromosome.fitness = fitness;
  return fitness;
}

/**
 * Calculates fitness for particle
 * Uses global calculator if available (fast), otherwise falls back to legacy method (slow)
 */
export function calculateParticleFitness(
  particle: Particle,
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  incrementFitnessEvaluations();
  let fitness: number;
  
  if (globalCalculator) {
    // Use optimized cached calculator (FAST - 10-100x faster)
    fitness = globalCalculator.calculateFitness(particle.position);
  } else {
    // Legacy mode - recalculates min/max every time (SLOW)
    fitness = calculateFitness(particle.position, matrix, taskCount, serviceCount);
  }
  
  particle.fitness = fitness;
  
  // Update personal best
  if (fitness > particle.personalBestFitness) {
    particle.personalBest = [...particle.position];
    particle.personalBestFitness = fitness;
  }

  return fitness;
}

/**
 * Calculates the workflow-aware aggregated QoS score independently from the active
 * optimization mode. In energy-aware mode this is the QoS component before it is
 * combined with normalized energy.
 */
export function calculateAggregatedQoS(
  solution: number[],
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  if (globalCalculator) {
    return globalCalculator.calculateQoSFitness(solution);
  }

  return calculateFitness(solution, matrix, taskCount, serviceCount);
}

/**
 * Calculates aggregated response time (sum of response times)
 * CRITICAL: Skips NaN/missing values to avoid injecting fake zeros
 */
export function calculateResponseTime(
  solution: number[],
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  let total = 0;
  for (let task = 0; task < taskCount; task++) {
    const serviceIdx = solution[task];
    if (serviceIdx >= 0 && serviceIdx < serviceCount) {
      const value = getQualityValue(matrix, task, serviceIdx, QoSProperty.ResponseTime);
      // Skip NaN/missing values - only sum finite RT values
      if (Number.isFinite(value)) {
        total += value;
      }
    }
  }
  return total;
}

/**
 * Calculates energy consumption (scientifically correct)
 * Energy (J) = Power (W) × Time (s)
 * Formula: Energy = ((0.6 * RT + 0.4 * Lat) / 1000) * 3.0
 * where:
 *   - RT, Lat are in milliseconds (from QWS dataset)
 *   - P=3.0W (average cloud server power consumption)
 *   - /1000 converts milliseconds to seconds for correct unit conversion
 * 
 * CRITICAL: Only compute energy if BOTH RT and Latency are finite
 * 
 * Scientific basis: Energy = Power × Time
 * Units: Joules = Watts × Seconds
 */
export function calculateEnergy(
  solution: number[],
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  const alpha = 0.6;  // Weight for response time
  const beta = 0.4;   // Weight for latency
  const P = 3.0;      // Power consumption in Watts (typical cloud server)

  let totalEnergy = 0;
  for (let task = 0; task < taskCount; task++) {
    const serviceIdx = solution[task];
    if (serviceIdx >= 0 && serviceIdx < serviceCount) {
      const rt = getQualityValue(matrix, task, serviceIdx, QoSProperty.ResponseTime); // ms
      const lat = getQualityValue(matrix, task, serviceIdx, QoSProperty.Latency);     // ms
      // Only compute energy if BOTH RT and Latency are finite
      if (Number.isFinite(rt) && Number.isFinite(lat)) {
        // Convert ms to seconds: divide by 1000
        // Energy (J) = Power (W) × Time (s)
        const energy = ((alpha * rt + beta * lat) / 1000) * P;
        totalEnergy += energy;
      }
    }
  }
  return totalEnergy; // Returns energy in Joules (J)
}

/**
 * Clamps a value to [min, max] range
 */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Cached Cost Calculator - Precomputes service prices for performance
 * CRITICAL OPTIMIZATION: Precomputes pricePerService[] once instead of recalculating on every cost call
 * This provides significant speedup for large problems (O(serviceCount*taskCount) -> O(taskCount))
 */
export class CostCalculator {
  private pricePerService: number[] | null = null;
  private matrix: number[][][] | null = null;
  private taskCount: number = 0;
  private serviceCount: number = 0;
  private timeCostRate: number = 0.00001; // Cost per millisecond of execution time (secondary term)
  private basePriceMin: number = 0.0005;  // Minimum base price per service invocation (budget services)
  private basePriceMax: number = 0.002;    // Maximum base price per service invocation (premium services)

  /**
   * Initialize calculator with matrix - precomputes service prices
   * Computes pricePerService[serviceIdx] once by averaging availability/reliability across tasks
   * 
   * QWS dataset: Availability and Reliability are percentages (0-100)
   * Normalize to [0, 1] by dividing by 100 and clamping
   */
  initialize(matrix: number[][][], taskCount: number, serviceCount: number): void {
    this.matrix = matrix;
    this.taskCount = taskCount;
    this.serviceCount = serviceCount;
    
    // Precompute pricePerService[] ONCE (this was the bottleneck!)
    this.pricePerService = new Array(serviceCount);
    
    for (let serviceIdx = 0; serviceIdx < serviceCount; serviceIdx++) {
      let totalAvailability = 0;
      let totalReliability = 0;
      let count = 0;
      
      // Average availability and reliability across all tasks for this service
      for (let task = 0; task < taskCount; task++) {
        const availability = getQualityValue(matrix, task, serviceIdx, QoSProperty.Availability);
        const reliability = getQualityValue(matrix, task, serviceIdx, QoSProperty.Reliability);
        
        // QWS dataset: Availability and Reliability are percentages (0-100)
        // Normalize to [0, 1] with clamping to handle out-of-range values
        // Use Number.isFinite for better validation
        if (Number.isFinite(availability) && Number.isFinite(reliability)) {
          const a = clamp(availability / 100, 0, 1);  // Normalize percentage to [0,1]
          const r = clamp(reliability / 100, 0, 1);  // Normalize percentage to [0,1]
          totalAvailability += a;
          totalReliability += r;
          count++;
        }
      }
      
      // Calculate average quality score for this service
      const avgAvailability = count > 0 ? totalAvailability / count : 0;
      const avgReliability = count > 0 ? totalReliability / count : 0;
      const qualityScore = (avgAvailability + avgReliability) / 2;
      
      // Map quality score to price range: quality [0, 1] -> price [basePriceMin, basePriceMax]
      // Clamp qualityScore to [0, 1] for safety
      const clampedQualityScore = clamp(qualityScore, 0, 1);
      this.pricePerService[serviceIdx] = this.basePriceMin + (clampedQualityScore * (this.basePriceMax - this.basePriceMin));
    }
  }

  /**
   * Calculates execution cost using precomputed service prices
   * Runtime: O(taskCount) - much faster than recalculating prices every call
   * 
   * Cost Model: ServiceDependentCost + TimeBasedCost
   * Formula: Cost = Σⱼ [pricePerService[service[j]]] + (totalResponseTime × timeCostRate)
   * 
   * CRITICAL: totalResponseTime must only sum finite RT values (skip NaN)
   */
  calculateCost(solution: number[]): number {
    if (!this.pricePerService || !this.matrix) {
      throw new Error('CostCalculator not initialized. Call initialize() first.');
    }

    let totalServiceCost = 0;
    let totalResponseTime = 0;
    
    for (let task = 0; task < this.taskCount; task++) {
      const serviceIdx = solution[task];
      if (serviceIdx >= 0 && serviceIdx < this.serviceCount) {
        // Use precomputed price per service (stable across tasks)
        totalServiceCost += this.pricePerService[serviceIdx];
        
        // Calculate total response time for time-based cost (secondary term)
        // totalResponseTime must only sum finite RT values (skip NaN)
        const rt = getQualityValue(this.matrix, task, serviceIdx, QoSProperty.ResponseTime);
        if (Number.isFinite(rt)) {
          totalResponseTime += rt;
        }
      }
    }
    
    // Total cost = service-dependent cost (primary) + time-based cost (secondary)
    // This ensures cost is NOT purely time-derived, enabling true multi-objective optimization
    const totalCost = totalServiceCost + (totalResponseTime * this.timeCostRate);
    
    return totalCost;
  }
}

// Global cost calculator instance (set by optimizationService for performance)
let globalCostCalculator: CostCalculator | null = null;

/**
 * Sets the global cost calculator (called by optimizationService)
 */
export function setGlobalCostCalculator(calculator: CostCalculator): void {
  globalCostCalculator = calculator;
}

/**
 * Clears the global cost calculator
 */
export function clearGlobalCostCalculator(): void {
  globalCostCalculator = null;
}

/**
 * Calculates execution cost (multi-objective model with service-dependent pricing)
 * 
 * CRITICAL: Cost must NOT be purely time-derived for true multi-objective evaluation.
 * If cost is linear in responseTime, it becomes redundant with energy (which is also
 * time-based), eliminating the multi-objective trade-off between cost and energy.
 * 
 * Cost Model: ServiceDependentCost + TimeBasedCost
 * Formula: Cost = Σⱼ [pricePerService[service[j]]] + (totalResponseTime × timeCostRate)
 * 
 * This model ensures true multi-objective optimization:
 * - Service-dependent component: Different services have different base prices per invocation
 *   (e.g., premium services cost more, budget services cost less)
 *   This creates a trade-off: expensive services may have better QoS but higher cost
 * - Time-based component: Secondary cost factor for compute time (kept as secondary term)
 * 
 * Service Pricing Model:
 *   - Premium services (high availability/reliability): Higher base price
 *   - Budget services (lower QoS): Lower base price
 *   - Price derived from normalized service quality metrics (availability, reliability)
 *   - QWS dataset: Availability and Reliability are percentages (0-100), normalized to [0,1]
 * 
 * Pricing Stability:
 *   - Price is computed PER SERVICE (not per task) for consistency
 *   - If matrix differs by task, availability/reliability are averaged across all tasks for each service
 *   - This ensures the same service always has the same price regardless of which task uses it
 * 
 * Performance Optimization:
 *   - Uses cached CostCalculator if available (FAST - O(taskCount))
 *   - Falls back to legacy method if calculator not initialized (SLOW - O(serviceCount*taskCount))
 * 
 * Units: Cost units (relative comparison - exact values matter less than consistency)
 * 
 * Note: This model provides true multi-objective trade-offs between:
 *   - Cost vs Energy (service pricing vs time-based energy)
 *   - Cost vs Fitness (service quality vs service price)
 */
export function calculateCost(
  solution: number[],
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  if (globalCostCalculator) {
    // Use optimized cached calculator (FAST - O(taskCount))
    return globalCostCalculator.calculateCost(solution);
  }
  
  // Legacy mode - recalculates prices every time (SLOW - O(serviceCount*taskCount))
  // This fallback is kept for backward compatibility
  const basePriceMin = 0.0005;   // Minimum base price per service invocation (budget services)
  const basePriceMax = 0.002;    // Maximum base price per service invocation (premium services)
  const timeCostRate = 0.00001;   // Cost per millisecond of execution time (secondary term)
  
  // Precompute pricePerService[serviceIdx] - stable per service across all tasks
  const pricePerService: number[] = new Array(serviceCount);
  
  for (let serviceIdx = 0; serviceIdx < serviceCount; serviceIdx++) {
    let totalAvailability = 0;
    let totalReliability = 0;
    let count = 0;
    
    // Average availability and reliability across all tasks for this service
    for (let task = 0; task < taskCount; task++) {
      const availability = getQualityValue(matrix, task, serviceIdx, QoSProperty.Availability);
      const reliability = getQualityValue(matrix, task, serviceIdx, QoSProperty.Reliability);
      
      // QWS dataset: Availability and Reliability are percentages (0-100)
      // Normalize to [0, 1] with clamping to handle out-of-range values
      // Use Number.isFinite for better validation
      if (Number.isFinite(availability) && Number.isFinite(reliability)) {
        const a = clamp(availability / 100, 0, 1);  // Normalize percentage to [0,1]
        const r = clamp(reliability / 100, 0, 1);  // Normalize percentage to [0,1]
        totalAvailability += a;
        totalReliability += r;
        count++;
      }
    }
    
    // Calculate average quality score for this service
    const avgAvailability = count > 0 ? totalAvailability / count : 0;
    const avgReliability = count > 0 ? totalReliability / count : 0;
    const qualityScore = (avgAvailability + avgReliability) / 2;
    
    // Map quality score to price range: quality [0, 1] -> price [basePriceMin, basePriceMax]
    // Clamp qualityScore to [0, 1] for safety
    const clampedQualityScore = clamp(qualityScore, 0, 1);
    pricePerService[serviceIdx] = basePriceMin + (clampedQualityScore * (basePriceMax - basePriceMin));
  }
  
  // Calculate total service cost using precomputed prices
  let totalServiceCost = 0;
  let totalResponseTime = 0;
  
  for (let task = 0; task < taskCount; task++) {
    const serviceIdx = solution[task];
    if (serviceIdx >= 0 && serviceIdx < serviceCount) {
      // Use precomputed price per service (stable across tasks)
      totalServiceCost += pricePerService[serviceIdx];
      
      // Calculate total response time for time-based cost (secondary term)
      // totalResponseTime must only sum finite RT values (skip NaN)
      const rt = getQualityValue(matrix, task, serviceIdx, QoSProperty.ResponseTime);
      if (Number.isFinite(rt)) {
        totalResponseTime += rt;
      }
    }
  }
  
  // Total cost = service-dependent cost (primary) + time-based cost (secondary)
  // This ensures cost is NOT purely time-derived, enabling true multi-objective optimization
  const totalCost = totalServiceCost + (totalResponseTime * timeCostRate);
  
  return totalCost;
}
